import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { ButtonGroup, CircularProgress, Tooltip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import * as poiState from '../../pointOfInterest/state';
import { useCuboidState } from '../hooks';
import { useAnalysisCuboidActions } from '../hooks/useAnalysisCuboidActions';
import { OutlinedIconButton } from '@/modules/cuboids/components/CuboidToolbar/styles';
import {
  AllPointOfInterestsForDataLoaderDocument,
  CuboidInput,
  SelectedPointOfInterestDocument,
  useUpdatePointOfInterestLocationMutation,
} from '@/__generated__/graphql';
import { cuboids, updateVoiLocationPosition as updateVoiLocationPositionAtom } from '../state';
import { viewerCurrentSpherical as viewerCurrentSphericalAtom } from '@/state';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import { extractErrorMessageFromUnknownError } from '@/utils/error';

export const CuboidDoneButtonGroup = () => {
  const cuboidState = useCuboidState();

  const currentCuboid = useAtomValue(cuboidState.currentCuboid);
  const [updateVoiLocationPosition, setUpdateVoiLocationPosition] = useAtom(
    updateVoiLocationPositionAtom
  );
  const viewerCurrentSpherical = useAtomValue(viewerCurrentSphericalAtom);
  const selectedPointOfInterestId = useAtomValue(poiState.selectedPointOfInterestId);
  const setCuboids = useSetAtom(cuboids);

  const { cancelAddingCuboid, doneAddingCuboid, stopEditingPoiCuboids } =
    useAnalysisCuboidActions();

  const { showSnackBar } = useSnackBarMessage({ variant: 'filled' });

  const editPointOfInterest = useAtomValue(poiState.editPointOfInterest);
  const [updatePoiLocation, { loading }] = useUpdatePointOfInterestLocationMutation({
    refetchQueries: [AllPointOfInterestsForDataLoaderDocument, SelectedPointOfInterestDocument],
  });

  const handleCancelCuboid = () => {
    const isEditingPoi = editPointOfInterest?.formState === 'Update-General';
    if (isEditingPoi) {
      stopEditingPoiCuboids(editPointOfInterest.pointOfInterest);
    } else {
      cancelAddingCuboid();
    }
  };

  const onConfirm = async () => {
    if (
      updateVoiLocationPosition && // Main check - indicates if the user has clicked 'update location' on a VOI
      selectedPointOfInterestId && // null check
      currentCuboid // null check
    ) {
      const cuboidInput: CuboidInput = {
        position: currentCuboid.position,
        rotation: currentCuboid.rotation,
        scale: currentCuboid.scale,
      };

      try {
        await updatePoiLocation({
          variables: {
            input: {
              pointOfInterestId: selectedPointOfInterestId,
              locationId: viewerCurrentSpherical?.id,
              point: {
                x: updateVoiLocationPosition[0],
                y: updateVoiLocationPosition[1],
                z: updateVoiLocationPosition[2],
              },
              cuboid: cuboidInput,
            },
          },
        });
        setCuboids((prev) => {
          const copy = prev.asMutableStateMap();
          copy.set(selectedPointOfInterestId, {
            id: selectedPointOfInterestId,
            position: cuboidInput.position as [number, number, number],
            rotation: cuboidInput.rotation as [number, number, number],
            scale: cuboidInput.scale as [number, number, number],
          });
          return copy.asStateMap();
        });
      } catch (error) {
        const message = extractErrorMessageFromUnknownError(error);
        showSnackBar(message || 'Failed to update cuboid', 'error');
      } finally {
        setUpdateVoiLocationPosition(undefined);
        cancelAddingCuboid();
      }
    }
    doneAddingCuboid();
  };

  return (
    <>
      <ButtonGroup sx={{ paddingBottom: '10px' }}>
        <Tooltip title="Cancel">
          <OutlinedIconButton onClick={handleCancelCuboid} disabled={!currentCuboid || loading}>
            <CloseIcon style={{ color: '#fff' }} />
          </OutlinedIconButton>
        </Tooltip>

        <Tooltip title={loading ? 'Loading...' : 'Done'}>
          <OutlinedIconButton onClick={onConfirm} disabled={!currentCuboid || loading}>
            {loading ? (
              <CircularProgress size={20} color="secondary" />
            ) : (
              <SaveIcon style={{ color: '#fff' }} />
            )}
          </OutlinedIconButton>
        </Tooltip>
      </ButtonGroup>
    </>
  );
};
